import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Col, Row, Typography, notification, Checkbox } from 'antd'
import { useTranslation } from 'react-i18next'

import { StepsPage } from '../structure'
import { useAuth } from '../context/AuthProvider'
import { useUploadProcess } from '../context/UploadProcessProvider'
import { License, Slider, ContractBtn } from '../components'
import { HTMLText, InfoRectangle, Loading } from '../components/common'
import { rpc } from '../api/requests'
import { getLicensesUrl } from '../api/consts'

const { Paragraph, Title } = Typography
const STEP_NUMBER = 1

const Contract = () => {
  const { t } = useTranslation()
  const { data, setValues } = useUploadProcess()
  const { token, apiHost } = useAuth()
  const params = useParams()
  const id = parseInt(params.id)
  const submission_id = parseInt(params.submission_id)
  const navigate = useNavigate()

  const [licenses, setLicenses] = useState({})

  useEffect(() => {
    if (token) {
      getDisplayLicense((response) => {
        if (!(response && response.option)) {
          // license not accepted?
          getAvailableLicenses((result) => {
            if (result['available_licenses']) {
              setLicenses(result)
            }
          })
        } else {
          setValues({ contract_option: response.option })
        }
      })
    }
  }, [token])

  const displayError = (message) => {
    notification.warning({
      message: t(message),
      placement: 'bottomLeft',
    })
  }
  async function get(method, data, onSuccess) {
    const result = await rpc(getLicensesUrl(apiHost), token, {
      method: method,
      document_id: submission_id,
      ...data,
    })
    if (result['ERROR']) {
      displayError(result['ERROR']['ID'])
    } else {
      console.log('Success:', result)
      onSuccess(result[0])
    }
    return true
  }

  const getDisplayLicense = (onSuccess) => {
    return get('display', {}, onSuccess)
  }

  const getAvailableLicenses = (onSuccess) => {
    return get('available', {}, onSuccess)
  }

  const acceptLicense = (name, onSuccess) => {
    return get(
      'accept',
      {
        accept: 'yes',
        option: name,
        license_short_name: licenses['short_name'],
        license_language_id: licenses['language_id'],
      },
      onSuccess
    )
  }

  const handleAcceptContract = (option) => {
    const { name } = licenses['available_licenses'][option]
    acceptLicense(name, () => {
      setValues({ contract_option: name })
      setLicenses({})
    })
  }

  return (
    <StepsPage
      step={STEP_NUMBER}
      className="upload-step2 pm"
      title={t('Royalties.ChooseContract')}
    >
      {!(data.contract_option || licenses.available_licenses) && <Loading />}
      {data.contract_option && (
        <DisplayLicense
          name={data.contract_option}
          onNextStep={() => navigate(`/${submission_id}/${id}/author`)}
        />
      )}
      {licenses.available_licenses && (
        <AvailableLicenses
          licenses={licenses.available_licenses}
          handleAcceptContract={handleAcceptContract}
        />
      )}
    </StepsPage>
  )
}
export default Contract

const AvailableLicenses = ({ licenses, handleAcceptContract }) => {
  const [acceptContract, setAcceptContract] = useState(true)
  const [option, setOption] = useState()

  useEffect(() => {
    const best_option_idx = licenses.findIndex((license) => license.best_option)
    if (best_option_idx !== -1) setOption(best_option_idx)
  }, [licenses])

  const { t } = useTranslation()

  return (
    <>
      <Paragraph>
        <HTMLText
          html={
            t('Royalties.FreeOfCharge') +
            '<br />' +
            t('Royalties.EbookPaperback')
          }
        />
      </Paragraph>
      <br />
      <Row gutter={48} align="middle" justify="center">
        <Slider onSlide={setOption} current={option} length={licenses.length}>
          {licenses.map((license, idx) => (
            <License
              key={license.name}
              selected={option === idx ? 'selected' : ''}
              onClick={() => setOption(idx)}
              {...license}
            />
          ))}
        </Slider>
      </Row>
      <hr className="dotted-line" />
      <Row gutter={[48, 20]} align="middle" justify="space-around">
        <Col>
          <ContractBtn
            btnType="default"
            link={t('ContractAccepted.DownloadContract.link')}
            text={t('Royalties.ShowContract.button')}
          />
        </Col>
        <Col style={{ borderRadius: '8px', backgroundColor: 'white' }}>
          <Paragraph
            className="paragraph-sb checkbox"
            style={{ width: '250px' }}
          >
            <Checkbox
              checked={acceptContract}
              onClick={() => setAcceptContract(!acceptContract)}
            >
              {t('Royalties.UnderstoodContract')}
            </Checkbox>
          </Paragraph>
        </Col>
        <Col>
          <Button
            type="primary"
            block
            disabled={!acceptContract}
            className="mt0 mb0"
            onClick={() => handleAcceptContract(option)}
          >
            {t('Royalties.AcceptContract.button')}
          </Button>
        </Col>
      </Row>
      <br></br>
      <br></br>
      <Paragraph className="paragraph-s">
        <HTMLText html={t('Royalties.RoyaltyTable')} />
      </Paragraph>
    </>
  )
}

const DisplayLicense = ({ name, onNextStep }) => {
  const { t } = useTranslation()
  return (
    <InfoRectangle img="images/contract/accepted.webp">
      <Title level={2}>
        <HTMLText
          html={t('ContractAccepted.ChosenOption').replace(
            '{OPTION}',
            t(`Royalties.${name}`)
          )}
        />
      </Title>
      <ContractBtn
        btnType="link"
        link={t('ContractAccepted.DownloadContract.link')}
        icon={true}
        text={t('ContractAccepted.DownloadContract')}
      />
      <Title level={3}>{t('ContractAccepted.WhatHappensNext')}</Title>
      <Col xs={24} sm={14} xl={16}>
        <Paragraph style={{ textAlign: 'center' }}>
          {t('ContractAccepted.StartReviewing')}
        </Paragraph>
      </Col>
      <Button type="primary" onClick={onNextStep}>
        {t('TextUploaded.Continue.button')}
      </Button>
    </InfoRectangle>
  )
}
